import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '../services/firebase';
import { dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, endOfWeek, addDays, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import { subscribeToTenantAppointments, updateAppointment, checkSlotAvailability, subscribeToAvailableDates, subscribeToEmployees, bookSlotViaFunction, checkUserAppointmentLimit, getBusinessData, subscribeToAppConfig, fetchMyAppointmentsViaFunction, fetchTenantAppointmentsViaFunction, fetchMyAppointmentsHttp, getEffectiveAvailability, getEffectiveAvailabilityHttpClient, getAvailableDates, getAppointments, createAppointment } from '../services/firebase';
import { collection, onSnapshot, query as fsQuery, where as fsWhere, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { toast } from 'react-toastify';
import { FiCalendar, FiClock, FiUser, FiX, FiUsers } from 'react-icons/fi';
import LegalFooter from '../components/LegalFooter';
import { useLang } from '../i18n/lang';

import 'react-big-calendar/lib/css/react-big-calendar.css';

// Estilos personalizados para fechas no disponibles
const customStyles = `
  .unavailable-date {
    background-color: #f3f4f6 !important;
    opacity: 0.3 !important;
    cursor: not-allowed !important;
  }
  
  .available-date {
    background-color: #f0f9ff !important;
    opacity: 1 !important;
    cursor: pointer !important;
  }
  
  .unavailable-date .rbc-day-content {
    pointer-events: none;
  }
`;

const locales = {
  'es': es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Helpers robustos para fechas provenientes de Firestore/Admin SDK/strings
const toDateSafe = (value) => {
  try {
    if (value?.toDate) return value.toDate();
    if (value && typeof value === 'object' && typeof value._seconds === 'number') {
      return new Date(value._seconds * 1000);
    }
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
  } catch { }
  return null;
};

const getAppointmentStart = (apt) => {
  // Priorizar dateStr + time para evitar desplazamientos de zona horaria
  try {
    if (apt?.dateStr && apt?.time) {
      const [y, mo, d] = String(apt.dateStr).split('-').map(n => parseInt(n, 10));
      const [hh, mm] = String(apt.time).split(':').map(n => parseInt(n, 10));
      return new Date(y, (mo || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
    }
  } catch { }
  const d = apt?.date ? toDateSafe(apt.date) : null;
  if (d) return d;
  if (apt?.dateStr) return new Date(`${apt.dateStr}T00:00:00`);
  return null;
};

// Validación simple de hora "HH:mm" o "H:mm"
const isValidTimeString = (value) => {
  try {
    return /^\d{1,2}:\d{2}$/.test(String(value || ''));
  } catch { return false; }
};

// Horario laboral (9:00 AM - 6:00 PM)
const WORKDAY_MIN = new Date(1970, 0, 1, 8, 0, 0);
const WORKDAY_MAX = new Date(1970, 0, 1, 22, 0, 0);

const ClientBooking = () => {
  const { t } = useLang();
  const USE_CALLABLE_ONLY = true; // Modo estable: evitar parpadeos usando solo callables
  const { user: currentUser, userClaims } = useSimpleAuth();
  const location = useLocation();
  const targetUser = location.state?.targetUser;

  // No redirecciones de dominio aquí para no romper el contenedor PWA (standalone).

  // Si soy admin y hay targetUser, uso su ID. Si no, uso el mío.
  const isAdmin = userClaims?.role === 'admin' || userClaims?.role === 'globalAdmin' || userClaims?.isAdmin === true || userClaims?.businessAdmin === true;
  const effectiveUserId = (isAdmin && targetUser?.id) ? targetUser.id : currentUser?.uid;
  const effectiveUserEmail = (isAdmin && targetUser?.email) ? targetUser.email : currentUser?.email;
  const effectiveUserName = (isAdmin && targetUser?.displayName) ? targetUser.displayName : currentUser?.displayName;
  const effectiveUserPhone = (isAdmin && targetUser?.phone) ? targetUser.phone : '';

  const { tenantId, role } = userClaims || {};
  const isClient = role === 'client';

  // Sin distinciones por dominio: usar flujo estándar (callables + HTTP fallback)

  // Valores dinámicos de marca por negocio (con valores por defecto)
  const [appName, setAppName] = useState('Reserberry');
  const [logoUrl, setLogoUrl] = useState(null);
  const [welcomeMessage, setWelcomeMessage] = useState('Bienvenido a nuestro sistema de reservas');
  const [welcomeMessageEnabled, setWelcomeMessageEnabled] = useState(true);

  // Suscribirse a la configuración del Admin (logo, nombre y mensaje)
  useEffect(() => {
    const unsubscribe = subscribeToAppConfig((cfg) => {
      try {
        if (cfg?.appName) setAppName(cfg.appName);
        if (typeof cfg?.logoUrl === 'string') setLogoUrl(cfg.logoUrl || null);
        if (typeof cfg?.welcomeMessageEnabled === 'boolean') setWelcomeMessageEnabled(cfg.welcomeMessageEnabled);
        if (typeof cfg?.welcomeMessage === 'string') setWelcomeMessage(cfg.welcomeMessage);
      } catch { }
    });
    return unsubscribe;
  }, []);

  // Cargar datos del negocio (nombre, logo y mensajes) según tenantId
  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      try {
        const business = await getBusinessData(tenantId);
        if (business?.name) setAppName((prev) => prev || business.name);
        if (business?.logoUrl) setLogoUrl((prev) => prev || business.logoUrl);
        if (typeof business?.welcomeMessageEnabled === 'boolean') setWelcomeMessageEnabled((prev) => prev ?? business.welcomeMessageEnabled);
        if (business?.welcomeMessage) setWelcomeMessage((prev) => prev || business.welcomeMessage);
      } catch (e) {
        // Mantener valores por defecto si no hay datos
        console.log('Info negocio no disponible, usando valores por defecto');
      }
    })();
  }, [tenantId]);

  // Debug: Verificar tenantId al cargar el componente
  useEffect(() => {
    console.log('🔄 ClientBooking - TenantId del contexto:', tenantId);
    console.log('🔄 ClientBooking - Rol del usuario:', role);
    console.log('🔄 ClientBooking - Es cliente:', isClient);
    console.log('🔄 ClientBooking - Usuario actual:', currentUser);

    // Verificar si hay servicios en la base de datos (con filtro de tenant)
    if (tenantId) {
      console.log('🔍 ClientBooking - Verificando servicios del tenant:', tenantId);
      const servicesQuery = fsQuery(
        collection(db, 'services'),
        fsWhere('tenantId', '==', tenantId)
      );
      onSnapshot(servicesQuery, (snapshot) => {
        console.log('🔍 ClientBooking - Servicios del tenant:', snapshot.docs.length);
        snapshot.docs.forEach(doc => {
          console.log('🔍 Servicio:', { id: doc.id, data: doc.data() });
        });
      }, (error) => {
        // Manejar específicamente errores de permisos
        if (error.code === 'permission-denied' ||
          error.message?.includes('Missing or insufficient permissions') ||
          error.message?.includes('permission-denied')) {
          console.log('🔍 ClientBooking - Permisos insuficientes para acceder a servicios (esto es normal para clientes no aprobados)');
        } else {
          console.error('❌ ClientBooking - Error cargando servicios:', error);
        }
      });
    }
    try {
      trackEvent('booking_opened', { tenantId: tenantId || null, has_user: !!currentUser?.uid });
    } catch { }
  }, [tenantId, role, isClient, currentUser]);
  const [appointments, setAppointments] = useState([]);
  // Estados paralelos para evitar parpadeos
  const [appointmentsRealtime, setAppointmentsRealtime] = useState([]);
  const [appointmentsCallable, setAppointmentsCallable] = useState([]);
  const [allAppointmentsRealtime, setAllAppointmentsRealtime] = useState([]);
  const [allAppointmentsCallable, setAllAppointmentsCallable] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const myAptsLoadedRef = useRef(false);
  const allAptsLoadedRef = useRef(false);
  const preferCallableRef = useRef(false);
  const preferCallableAllRef = useRef(false);
  const [preferCallableAppointments, setPreferCallableAppointments] = useState(false);

  // Helper consistente: considerar "activa" solo pendiente/confirmed.
  // Cualquier otro estado (cancelada, completada, no_asiste, etc.) NO bloquea.
  const isCancelled = useCallback((apt) => {
    if (!apt) return false;
    const s = String(apt.status || '').toLowerCase().trim();
    if (apt.cancelled === true) return true;
    const isActive = s === 'pendiente' || s === 'pending' || s === 'confirmed';
    return !isActive;
  }, []);

  // Reconciliar ocultación local de slots con las citas reales del usuario
  useEffect(() => {
    try {
      const allowedKeys = new Set();
      const collect = (arr) => {
        (arr || []).forEach((a) => {
          if (!a || String((a.status || '')).toLowerCase() === 'cancelada') return;
          const d = getAppointmentStart(a);
          const ds = a.dateStr || (d ? format(d, 'yyyy-MM-dd') : '');
          let ts = '';
          if (typeof a.time === 'string' && a.time.includes(':')) {
            const parts = a.time.split(':');
            ts = `${String(parseInt(parts[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(parts[1] || '0', 10)).padStart(2, '0')}`;
          } else if (d) {
            ts = format(d, 'HH:mm');
          }
          if (ds && ts) allowedKeys.add(`${ds} ${ts}`);
        });
      };
      collect(appointments);
      // Añadir también desde allAppointments pero solo si son mías
      const myUid = effectiveUserId || null;
      const myEmail = (effectiveUserEmail || '').toLowerCase();
      (allAppointments || []).forEach((a) => {
        if (!a || String((a.status || '')).toLowerCase() === 'cancelada') return;
        const isMine = (myUid && a.userId === myUid) || (a.userEmail && String(a.userEmail).toLowerCase() === myEmail);
        if (!isMine) return;
        const d = getAppointmentStart(a);
        const ds = a.dateStr || (d ? format(d, 'yyyy-MM-dd') : '');
        let ts = '';
        if (typeof a.time === 'string' && a.time.includes(':')) {
          const parts = a.time.split(':');
          ts = `${String(parseInt(parts[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(parts[1] || '0', 10)).padStart(2, '0')}`;
        } else if (d) {
          ts = format(d, 'HH:mm');
        }
        if (ds && ts) allowedKeys.add(`${ds} ${ts}`);
      });

      // Desactivar y limpiar cualquier rastro de ocultado local de slots
      try { localStorage.removeItem('hiddenSlotsV1'); } catch { }
    } catch { }
  }, [appointments, allAppointments, currentUser]);

  // Refresco directo de citas del usuario
  const refreshMyAppointments = useCallback(async () => {
    try {
      if (!effectiveUserId) return;
      // Si soy admin impersonando, filtrar de la lista global ya suscrita
      // (evita llamadas a Functions que fallan por CORS).

      if (isAdmin && targetUser) {
        // Lógica especial admin: filtrar de allAppointments por userId o email del usuario objetivo
        const myEmail = (effectiveUserEmail || '').toLowerCase();
        const mine = (allAppointments || []).filter(apt => {
          const byUid = apt.userId === effectiveUserId;
          const byUserEmail = (apt.userEmail || '').toLowerCase() === myEmail;
          const byCustomerEmail = (apt.customerEmail || '').toLowerCase() === myEmail;
          return byUid || byUserEmail || byCustomerEmail;
        });
        setAppointmentsCallable(mine);
        setAppointments(mine);
        return mine;
      }

      // Carga directa desde Firestore (sin Cloud Functions)
      const q = fsQuery(
        collection(db, 'appointments'),
        fsWhere('tenantId', '==', tenantId),
        fsWhere('userId', '==', effectiveUserId)
      );
      const snap = await getDocs(q);
      const mine = snap.docs.map(d => ({ id: d.id, ...d.data() })) || [];
      setAppointmentsCallable(mine);
      setAppointments(mine);
      return mine;
    } catch { return []; }
  }, [effectiveUserId, effectiveUserEmail, isAdmin, targetUser, allAppointments, tenantId]);
  const [services, setServices] = useState([]); // Nuevo estado para servicios
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [availableDates, setAvailableDates] = useState({});
  const [effectiveByDate, setEffectiveByDate] = useState({}); // {dateStr: string[]}
  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 3);
    return d;
  });
  const [rangeEnd, setRangeEnd] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 35);
    return d;
  });
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null); // null = Indiferente
  // Flujo móvil paso a paso (servicio → profesional → semana)
  const [mobileStep, setMobileStep] = useState(1);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  const [isBooking, setIsBooking] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null); // clave única: `${dateStr} ${timeStr}`
  const [appointmentLimit, setAppointmentLimit] = useState({ currentCount: 0, maxAllowed: 3, canBook: true });
  const [userInfo, setUserInfo] = useState(null);

  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Obtener maxAllowed desde backend (una vez por usuario)
  useEffect(() => {
    if (!effectiveUserId) return;
    (async () => {
      try {
        const limit = await checkUserAppointmentLimit(effectiveUserId);
        setAppointmentLimit(prev => ({ ...prev, maxAllowed: limit.maxAllowed }));
        console.log('🔄 Límite máximo por usuario:', limit.maxAllowed);
      } catch (error) {
        console.error('Error cargando límite de citas:', error);
      }
    })();
  }, [currentUser]);

  // Cargar información del usuario desde Firestore
  useEffect(() => {
    if (!effectiveUserId) return;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', effectiveUserId));
        if (userDoc.exists()) {
          setUserInfo(userDoc.data());
          console.log('📱 Información del usuario cargada:', userDoc.data());
        }
      } catch (error) {
        // Manejar específicamente errores de permisos
        if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
          console.log('Permisos insuficientes para acceder a datos del usuario actual');
          // Usar datos básicos del usuario autenticado
          setUserInfo({
            displayName: effectiveUserName || '',
            email: effectiveUserEmail || '',
            phone: effectiveUserPhone || '',
            isAdmin: false
          });
        } else {
          console.error('Error cargando información del usuario:', error);
        }
      }
    })();
  }, [effectiveUserId, effectiveUserName, effectiveUserEmail, effectiveUserPhone]);

  // Calcular localmente el número de citas activas del usuario (pendientes futuras)
  useEffect(() => {
    if (!effectiveUserId) return;
    const now = new Date();
    // Contar exactamente lo que se muestra en la lista: citas pendientes y FUTURAS
    const count = (appointments || []).filter((apt) => {
      if (String((apt.status || '').toLowerCase()) !== 'pendiente') return false;
      const start = getAppointmentStart(apt);
      return !!(start && start > now);
    }).length;
    setAppointmentLimit(prev => ({ ...prev, currentCount: count, canBook: count < (prev.maxAllowed || 3) }));
  }, [appointments, currentUser]);

  // Confirmación personalizada para cancelar cita
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const requestCancelAppointment = (appointmentId) => {
    setConfirmCancelId(appointmentId);
    setShowConfirmCancel(true);
  };

  const performCancelAppointment = async () => {
    const appointmentId = confirmCancelId;
    setShowConfirmCancel(false);
    if (!appointmentId) return;
    try {
      await updateAppointment(appointmentId, { status: 'cancelada' });
      toast.success('✅ Cita cancelada exitosamente');
      // Optimista: marcar la cita como cancelada en el estado global del tenant
      try {
        // Mis citas
        setAppointments(prev => Array.isArray(prev)
          ? prev.map(a => (a && a.id === appointmentId) ? { ...a, status: 'cancelada' } : a)
          : prev);
        setAppointmentsCallable(prev => Array.isArray(prev)
          ? prev.map(a => (a && a.id === appointmentId) ? { ...a, status: 'cancelada' } : a)
          : prev);
        setAllAppointments(prev => Array.isArray(prev)
          ? prev.map(a => (a && a.id === appointmentId) ? { ...a, status: 'cancelada' } : a)
          : prev);
        setAllAppointmentsCallable(prev => Array.isArray(prev)
          ? prev.map(a => (a && a.id === appointmentId) ? { ...a, status: 'cancelada' } : a)
          : prev);
      } catch { }
      if (effectiveUserId) {
        const now = new Date();
        const remaining = (appointments || []).filter((apt) => {
          if ((apt.id || '') === appointmentId) return false;
          if ((apt.status || '').toString().toLowerCase().trim() !== 'pendiente') return false;
          let start = null;
          try {
            if (apt.date?.toDate) start = apt.date.toDate();
            else if (apt.date) start = new Date(apt.date);
            else if (apt.dateStr && apt.time) {
              const [y, mo, d] = String(apt.dateStr).split('-').map(n => parseInt(n, 10));
              const [hh, mm] = String(apt.time).split(':').map(n => parseInt(n, 10));
              start = new Date(y, (mo || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
            }
          } catch { start = null; }
          return !!(start && start > now);
        }).length;
        setAppointmentLimit(prev => ({ ...prev, currentCount: remaining, canBook: remaining < (prev.maxAllowed || 3) }));
      }
      // Limpiar ocultación local del slot cancelado para que reaparezca
      try {
        const canceled = (appointments || []).find(a => a.id === appointmentId);
        if (canceled) {
          const d = getAppointmentStart(canceled);
          const ds = canceled.dateStr || (d ? format(d, 'yyyy-MM-dd') : '');
          let ts = '';
          if (typeof canceled.time === 'string' && canceled.time.includes(':')) {
            const parts = canceled.time.split(':');
            ts = `${String(parseInt(parts[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(parts[1] || '0', 10)).padStart(2, '0')}`;
          } else if (d) {
            ts = format(d, 'HH:mm');
          }
          if (ds && ts) {
            // Ocultado local desactivado
          }
        }
      } catch { }
      // Refrescar citas
      try { await refreshMyAppointments(); } catch { }
      // Forzar recarga inmediata de las citas del tenant para que el empleado quede libre ya
      try {
        const items = await fetchTenantAppointmentsViaFunction();
        if (Array.isArray(items)) {
          setAllAppointments(items);
          setAllAppointmentsCallable(items);
        }
      } catch { }
      // Forzar recarga de disponibilidad para que el slot reaparezca
      try {
        // Re-suscribir a availableDates invalidando la clave de semana
        const ws = startOfWeek(new Date(), { weekStartsOn: 1 });
        setWeekStart(new Date(ws.getTime()));
        // Bump de rango para reprocesar
        setRangeStart(prev => new Date(prev.getTime() - 1));
        setRangeEnd(prev => new Date(prev.getTime() + 1));
      } catch { }
    } catch (error) {
      toast.error('❌ Error al cancelar la cita');
      console.error('Error:', error);
    } finally {
      setConfirmCancelId(null);
    }
  };

  // Cargar servicios desde Firestore
  useEffect(() => {
    if (!tenantId) {
      console.log('⚠️ ClientBooking - No hay tenantId, saltando carga de servicios');
      return;
    }

    console.log('🔄 ClientBooking - Cargando servicios...');
    console.log('🔄 ClientBooking - TenantId para servicios:', tenantId);

    let q = collection(db, 'services');
    q = fsQuery(q, fsWhere('tenantId', '==', tenantId));
    console.log('🔄 ClientBooking - Query construida:', q);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('🔄 ClientBooking - Snapshot de servicios recibido:', snapshot.docs.length, 'documentos');
      console.log('🔄 ClientBooking - Documentos raw:', snapshot.docs.map(doc => ({ id: doc.id, data: doc.data() })));

      const servicesData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(service => service.isActive === true);
      console.log('✅ ClientBooking - Servicios cargados (filtrados por isActive):', servicesData);
      setServices(servicesData);
    }, (error) => {
      console.error('❌ ClientBooking - Error cargando servicios:', error);
    });

    return unsubscribe;
  }, [tenantId]);

  // Cargar empleados
  useEffect(() => {
    if (!tenantId) {
      console.log('⚠️ ClientBooking - No hay tenantId, saltando carga de empleados');
      return;
    }

    console.log('🔄 ClientBooking - Cargando empleados...');
    const unsub = subscribeToEmployees((list) => {
      console.log('🔄 ClientBooking - Empleados recibidos:', list);
      const activeEmployees = list.filter(e => e.active !== false);
      console.log('✅ ClientBooking - Empleados activos:', activeEmployees);
      setEmployees(activeEmployees);
    });
    return unsub;
  }, [tenantId]);

  // Cargar citas del usuario (modo estable por callable)
  useEffect(() => {
    if (!effectiveUserId) return;
    let intervalId = null;
    const load = async () => { await refreshMyAppointments(); };
    load();
    if (USE_CALLABLE_ONLY) {
      intervalId = setInterval(load, 10000); // refresco cada 10s
      return () => { try { clearInterval(intervalId); } catch { } };
    }
    return () => { };
  }, [currentUser, refreshMyAppointments]);

  // Exponer para diagnóstico rápido en consola
  useEffect(() => {
    try { window.myAppointments = appointments; } catch { }
  }, [appointments]);

  // Cargar todas las citas (para bloquear slots)
  // 1) Suscripción en tiempo real del tenant (si permisos lo permiten)
  useEffect(() => {
    const unsub = subscribeToTenantAppointments((items) => {
      if (Array.isArray(items)) {
        setAllAppointments(items);
        setAllAppointmentsCallable(items);
      }
    });
    return unsub;
  }, []);

  // 2) Fallback por callable (refresco periódico) para entornos donde falle la suscripción
  useEffect(() => {
    // Desactivado para evitar CORS: la suscripción en tiempo real ya mantiene actualizado allAppointments.
    return () => { };
  }, []);

  // Refresco acelerado en móvil cuando se está eligiendo hora o hay profesional seleccionado
  useEffect(() => {
    // Desactivado para evitar CORS: confiar en la suscripción en tiempo real.
    return () => { };
  }, [isMobile, mobileStep, selectedEmployeeId]);

  // Suscribirse a fechas disponibles para el rango visible del calendario
  useEffect(() => {
    if (!tenantId) {
      // Esperar a tener tenant antes de suscribir
      return () => { };
    }
    console.log('🔍 Suscribiéndose a fechas disponibles...', { tenantId, start: format(rangeStart, 'yyyy-MM-dd'), end: format(rangeEnd, 'yyyy-MM-dd') });
    const unsubscribe = subscribeToAvailableDates((dates) => {
      console.log('📅 Fechas disponibles recibidas:', Object.keys(dates).length);
      setAvailableDates(dates);
    }, rangeStart, rangeEnd);
    return unsubscribe;
  }, [tenantId, rangeStart, rangeEnd]);

  // Cargar disponibilidad efectiva por cada día visible de la semana actual (cálculo local, sin Functions)
  useEffect(() => {
    const days = Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i));
    (async () => {
      try {
        const map = {};
        days.forEach((d) => {
          const ds = format(d, 'yyyy-MM-dd');
          const slots = Array.isArray(availableDates?.[ds]?.slots) ? availableDates[ds].slots : [];
          map[ds] = slots;
        });
        setEffectiveByDate(map);
      } catch { }
    })();
  }, [weekStart, tenantId, availableDates]);

  // Generar slots de tiempo disponibles (DESACTIVADO - Solo usar nuevo sistema)
  const timeslots = useMemo(() => {
    // NO generar slots automáticamente - solo usar fechas explícitamente abiertas
    return [];
  }, []);

  // Verificar si una fecha está disponible para reservas
  const isDateAvailable = useCallback((date) => {
    // Normalizar la fecha a UTC para evitar problemas de zona horaria
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dateStr = format(normalizedDate, 'yyyy-MM-dd');
    const dateData = availableDates[dateStr];

    console.log(`🔍 Verificando disponibilidad para ${dateStr}:`, dateData);
    console.log(`🔍 Fecha original:`, date);
    console.log(`🔍 Fecha normalizada:`, normalizedDate);

    // Considerar disponible si hay slots, aunque falte el flag 'available'
    const isAvailable = Boolean(
      dateData &&
      Array.isArray(dateData.slots) &&
      dateData.slots.length > 0
    );

    console.log(`✅ Fecha ${dateStr} disponible: ${isAvailable} (slots: ${dateData?.slots?.length || 0})`);
    return isAvailable;
  }, [availableDates]);

  // Obtener slots disponibles para una fecha específica
  const getAvailableSlotsForDate = useCallback((date) => {
    // Normalizar la fecha a UTC para evitar problemas de zona horaria
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dateStr = format(normalizedDate, 'yyyy-MM-dd');
    const dateData = availableDates[dateStr];
    return dateData?.slots || [];
  }, [availableDates]);


  // Convertir citas a eventos del calendario
  const events = useMemo(() => {
    // Ocultado local desactivado: no leer ni aplicar hiddenSlotsV1
    const hiddenSlots = {};
    const now = new Date();
    // Conjunto de claves de horarios ya reservados por el usuario (dateStr time)
    const myTaken = new Set(
      (appointments || []).filter(a => !isCancelled(a)).map((a) => {
        const d = getAppointmentStart(a);
        const ds = a.dateStr || (d ? format(d, 'yyyy-MM-dd') : '');
        // Normalizar hora a HH:mm aunque venga como '9:10'
        let ts = '';
        if (typeof a.time === 'string' && a.time.includes(':')) {
          const parts = a.time.split(':');
          const hh = parts[0] != null ? String(parseInt(parts[0], 10)).padStart(2, '0') : '00';
          const mm = parts[1] != null ? String(parseInt(parts[1], 10)).padStart(2, '0') : '00';
          ts = `${hh}:${mm}`;
        } else if (d) {
          ts = format(d, 'HH:mm');
        }
        return `${ds} ${ts}`;
      })
    );
    // Refuerzo: si por permisos no llegan "appointments", usar allAppointments para deducir mis horas
    try {
      const myTakenFromAll = (allAppointments || [])
        .filter(a => a && !isCancelled(a) && (a.userId === (currentUser && currentUser.uid) || (a.userEmail && currentUser && a.userEmail.toLowerCase() === currentUser.email.toLowerCase())))
        .map((a) => {
          const d = getAppointmentStart(a);
          const ds = a.dateStr || (d ? format(d, 'yyyy-MM-dd') : '');
          let ts = '';
          if (typeof a.time === 'string' && a.time.includes(':')) {
            const parts = a.time.split(':');
            const hh = parts[0] != null ? String(parseInt(parts[0], 10)).padStart(2, '0') : '00';
            const mm = parts[1] != null ? String(parseInt(parts[1], 10)).padStart(2, '0') : '00';
            ts = `${hh}:${mm}`;
          } else if (d) {
            ts = format(d, 'HH:mm');
          }
          return `${ds} ${ts}`;
        });
      for (const k of myTakenFromAll) myTaken.add(k);
    } catch { }
    const userApts = appointments
      .filter(a => !isCancelled(a))
      .map(apt => {
        const start = getAppointmentStart(apt) || new Date();
        const end = new Date(start.getTime() + 30 * 60000);
        return {
          id: `apt-${apt.id}`,
          title: `${apt.type} · ${apt.userName}`,
          start,
          end,
          resource: { ...apt, kind: 'appointment' }
        };
      });

    // Bloqueo propio por duración: construir un set con todos los sub-tramos (10m) ocupados por MIS citas
    const myBusySet = new Set(); // claves "yyyy-MM-dd HH:mm" de todos los tramos ocupados por el usuario
    const addBusyFromList = (list) => {
      (list || []).filter(a => !isCancelled(a)).forEach((a) => {
        const baseDate = a.date?.toDate ? a.date.toDate() : (a.dateStr && a.time
          ? new Date(parseInt(a.dateStr.split('-')[0], 10), parseInt(a.dateStr.split('-')[1], 10) - 1, parseInt(a.dateStr.split('-')[2], 10), parseInt(a.time.split(':')[0], 10), parseInt(a.time.split(':')[1], 10), 0, 0)
          : new Date(a.date));
        if (!baseDate || isNaN(baseDate.getTime())) return;
        const durationMin = typeof a.duration === 'number' ? a.duration : 30;
        for (let min = 0; min < durationMin; min += 10) {
          const slotTime = new Date(baseDate.getTime() + min * 60000);
          const dateKey = format(slotTime, 'yyyy-MM-dd');
          const timeKey = format(slotTime, 'HH:mm');
          myBusySet.add(`${dateKey} ${timeKey}`);
        }
      });
    };
    addBusyFromList(appointments);
    try {
      const mineAll = (allAppointments || []).filter(a => a && !isCancelled(a) && (a.userId === effectiveUserId || (a.userEmail && effectiveUserEmail && a.userEmail.toLowerCase() === effectiveUserEmail.toLowerCase())));
      addBusyFromList(mineAll);
    } catch { }

    // Construir conteos por fecha/hora y reservas por empleado
    const takenCounts = {}; // {dateStr: {time: count}}
    const takenByEmployee = {}; // {dateStr: {time: Set(employeeId)}}
    allAppointments
      .filter(a => !isCancelled(a))
      .forEach(a => {
        // Contabilizar ocupación para capacidad SOLO en el inicio del slot
        const baseDate = a.date?.toDate ? a.date.toDate() : (a.dateStr && a.time
          ? new Date(parseInt(a.dateStr.split('-')[0], 10), parseInt(a.dateStr.split('-')[1], 10) - 1, parseInt(a.dateStr.split('-')[2], 10), parseInt(a.time.split(':')[0], 10), parseInt(a.time.split(':')[1], 10), 0, 0)
          : new Date(a.date));
        const startDateKey = a.dateStr || format(baseDate, 'yyyy-MM-dd');
        const startTimeKey = a.time || format(baseDate, 'HH:mm');
        if (!takenCounts[startDateKey]) takenCounts[startDateKey] = {};
        if (!takenCounts[startDateKey][startTimeKey]) takenCounts[startDateKey][startTimeKey] = 0;
        takenCounts[startDateKey][startTimeKey] += 1;

        // Para bloqueo por profesional: expandir a todos los bloques de 10 min durante la cita
        if (!takenByEmployee[startDateKey]) takenByEmployee[startDateKey] = {};
        const durationMin = typeof a.duration === 'number' ? a.duration : 30;
        for (let min = 0; min < durationMin; min += 10) {
          const slotTime = new Date(baseDate.getTime() + min * 60000);
          const dateKey = format(slotTime, 'yyyy-MM-dd');
          const timeKey = format(slotTime, 'HH:mm');
          if (!takenByEmployee[dateKey]) takenByEmployee[dateKey] = {};
          if (!takenByEmployee[dateKey][timeKey]) takenByEmployee[dateKey][timeKey] = new Set();
          if (a.employeeId) takenByEmployee[dateKey][timeKey].add(a.employeeId);
        }
      });

    const slotEvents = [];
    Object.entries(availableDates || {}).forEach(([dateStr, value]) => {
      if (!Array.isArray(value.slots)) return;
      value.slots.forEach((slot) => {
        const timeStr = typeof slot === 'string' ? slot : slot?.time;
        if (!timeStr) return;
        // Normalizar hora del slot para comparar con myTaken
        const parts = String(timeStr).split(':');
        const timeNorm = `${String(parseInt(parts[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(parts[1] || '0', 10)).padStart(2, '0')}`;
        // Ocultado local desactivado
        const [h, m] = timeStr.split(':').map(n => parseInt(n, 10));
        const [y, mo, d] = dateStr.split('-').map(n => parseInt(n, 10));
        const start = new Date(y, mo - 1, d, h, m, 0, 0);
        // Ocultar si el usuario ya tiene una cita que ocupa este sub-tramo (se filtra también abajo con myBusySet)
        if (start < now) return; // solo futuros
        const end = new Date(start.getTime() + 30 * 60000);
        // Ocultar slot si el usuario ya tiene una cita que ocupa este sub-tramo (bloqueo por duración)
        if (myBusySet.has(`${dateStr} ${timeNorm}`)) return;
        // Capacidad
        const capacity = (typeof slot === 'object' && slot?.capacity != null)
          ? Number(slot.capacity)
          : Number(value.defaultCapacity || value.maxBookingsPerSlot || 1);
        const current = takenCounts[dateStr]?.[timeNorm] || 0;
        // Filtro por empleado seleccionado
        if (selectedEmployeeId) {
          const allowedEmployees = (typeof slot === 'object' && Array.isArray(slot.employees) && slot.employees.length > 0)
            ? slot.employees
            : null; // null => todos
          if (allowedEmployees && !allowedEmployees.includes(selectedEmployeeId)) return;
          // Bloquear si el profesional está ocupado en cualquier sub-tramo de la duración (30 min por defecto)
          const durationMin = typeof selectedService?.duration === 'number' ? selectedService.duration : 30;
          let busy = false;
          for (let min = 0; min < durationMin; min += 10) {
            const tKey = format(new Date(start.getTime() + min * 60000), 'HH:mm');
            if (takenByEmployee[dateStr]?.[tKey]?.has(selectedEmployeeId)) { busy = true; break; }
          }
          if (busy) return;
          if (current >= capacity) return;
        } else {
          // Indiferente: aplicar bloqueo por duración en sub-tramos de 10m para cualquier empleado
          if (current >= capacity) return;
          const allowedEmployees = (typeof slot === 'object' && Array.isArray(slot.employees) && slot.employees.length > 0)
            ? slot.employees
            : (Array.isArray(employees) ? employees.map(e => e.id) : []);
          const durationMin = typeof selectedService?.duration === 'number' ? selectedService.duration : 30;
          // Un empleado es válido si está libre en TODOS los sub-tramos de la duración
          const hasEmployeeFreeAcrossDuration = allowedEmployees.some((empId) => {
            for (let min = 0; min < durationMin; min += 10) {
              const tKey = format(new Date(start.getTime() + min * 60000), 'HH:mm');
              if (takenByEmployee[dateStr]?.[tKey]?.has(empId)) return false;
            }
            return true;
          });
          if (!hasEmployeeFreeAcrossDuration) return;
        }
        // Indiferente: pintar una vez por hora (no duplicar) solo si hay plazas
        if (current < capacity) {
          slotEvents.push({
            id: `slot-${dateStr}-${timeNorm}`,
            title: 'Disponible',
            start,
            end,
            resource: { kind: 'availableSlot', dateStr, timeStr: timeNorm, capacity, taken: current }
          });
        }
      });
    });

    return [...slotEvents, ...userApts];
  }, [appointments, allAppointments, availableDates, selectedEmployeeId]);

  // Verificar solapamiento con MIS citas (independiente del empleado)
  function hasOverlap(start, end) {
    return (appointments || []).some((apt) => {
      if (isCancelled(apt)) return false;
      const aptStart = getAppointmentStart(apt) || (apt.date?.toDate ? apt.date.toDate() : new Date(apt.date));
      const dur = typeof apt.duration === 'number' ? apt.duration : 30;
      const aptEnd = new Date(aptStart.getTime() + dur * 60000);
      return (start < aptEnd) && (end > aptStart);
    });
  }

  const handleSlotSelect = async (slotInfo) => {
    if (isBooking) return;
    if (!services || services.length === 0) {
      toast.error('❌ No hay servicios disponibles en este momento');
      return;
    }

    if (!selectedService) {
      toast.error('❌ Primero selecciona un servicio');
      // En móvil, regresar al paso 1 para elegir servicio
      setMobileStep(1);
      return;
    }

    const start = slotInfo.start;
    const end = new Date(start.getTime() + (typeof selectedService?.duration === 'number' ? selectedService.duration : 30) * 60000);
    const timeStr = format(start, 'HH:mm');
    const dateStr = format(start, 'yyyy-MM-dd');

    // Feedback visual inmediato
    setSelectedTimeSlot(`${dateStr} ${timeStr}`);

    // Refrescar mis citas justo antes de validar el tope para evitar falsos límites
    const fresh = await refreshMyAppointments();
    // Recalcular visualmente el contador con lo fresco
    try {
      const now = new Date();
      const count = (fresh || []).filter((apt) => {
        const st = (apt.status || '').toString().toLowerCase().trim();
        if (st !== 'pendiente') return false;
        const d = apt.date?.toDate ? apt.date.toDate() : (apt.date ? new Date(apt.date) : null);
        const start = d || (apt.dateStr && apt.time ? new Date(parseInt(apt.dateStr.split('-')[0], 10), parseInt(apt.dateStr.split('-')[1], 10) - 1, parseInt(apt.dateStr.split('-')[2], 10), parseInt(apt.time.split(':')[0], 10), parseInt(apt.time.split(':')[1], 10), 0, 0) : null);
        return !!(start && start > now);
      }).length;
      setAppointmentLimit(prev => ({ ...prev, currentCount: count, canBook: count < (prev.maxAllowed || 3) }));
    } catch { }
    // Verificar límite de citas por usuario con datos frescos (solo pendientes futuras)
    const limitCheck = await checkUserAppointmentLimit(currentUser.uid);
    // Salvaguarda: si el cliente muestra 1/3 pero la función devuelve bloqueado por un desajuste temporal, dejamos reservar.
    if (!limitCheck.canBook && appointmentLimit.currentCount < (appointmentLimit.maxAllowed || 3)) {
      console.warn('⚠️ Desajuste límite detectado. Front dice', appointmentLimit.currentCount, 'de', appointmentLimit.maxAllowed, 'pero backend bloquea. Permitimos continuar.');
    } else if (!limitCheck.canBook) {
      setSelectedTimeSlot(null);
      toast.error(`❌ Has alcanzado el límite máximo de ${limitCheck.maxAllowed} citas activas. Cancela alguna cita existente para reservar una nueva.`);
      return;
    }

    // Verificar si la fecha está disponible para reservas
    if (!isDateAvailable(start)) {
      setSelectedTimeSlot(null);
      toast.error('❌ Esta fecha no está disponible para reservas.');
      return;
    }

    // Verificar si el horario específico está disponible
    const dateData = availableDates[dateStr];
    const hasTime = Array.isArray(dateData?.slots) && dateData.slots.some((s) => (typeof s === 'string' ? s === timeStr : s?.time === timeStr));
    if (!hasTime) {
      setSelectedTimeSlot(null);
      toast.error(`❌ El horario ${timeStr} no está disponible para esta fecha`);
      return;
    }

    if (hasOverlap(start, end)) {
      setSelectedTimeSlot(null);
      toast.error('❌ Este horario ya está ocupado');
      return;
    }

    // Reserva optimizada
    try {
      setIsBooking(true);

      // Feedback inmediato al usuario
      toast.info('🔄 Reservando cita...', { autoClose: 1000 });

      const res = await bookSlotViaFunction({
        dateStr,
        time: timeStr,
        serviceId: selectedService.id,
        employeeId: selectedEmployeeId || null,
        type: selectedService.name,
        duration: typeof selectedService?.duration === 'number' ? selectedService.duration : 30,
        price: selectedService.price,
        userName: userInfo?.displayName || currentUser?.displayName || 'Usuario',
        userEmail: userInfo?.email || currentUser?.email || '',
        userPhone: userInfo?.phone || '',
        tenantId: tenantId,
        targetUserId: effectiveUserId, // Admin override
        clientName: userInfo?.displayName || currentUser?.displayName || 'Usuario',
        serviceName: selectedService.name,
        userId: effectiveUserId
      });
      if (!res || res.duplicated) {
        setSelectedTimeSlot(null);
        toast.error('❌ Ese horario ya no está disponible.');
        return;
      }
      toast.success('✅ ¡Cita reservada exitosamente!');
      // No ocultar el slot tras reservar (el backend controla aforo). Solo refrescar mis citas.
      try { localStorage.removeItem('hiddenSlotsV1'); } catch { }
      // Refrescar lista del usuario inmediatamente
      await refreshMyAppointments();
      // Reiniciar selección de hora y volver a inicio del flujo
      setSelectedSlot(null);
      setSelectedTimeSlot(null);
      setSelectedService(null);
      setSelectedEmployeeId(null);
      setMobileStep(1);
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { }
    } catch (error) {
      console.error('Error al reservar:', error);
      // No intentar escribir directo en Firestore (respeta reglas y evita ruido).
      setSelectedTimeSlot(null);
      toast.error('❌ No se pudo completar la reserva. Inténtalo de nuevo en unos minutos.');
    } finally {
      setIsBooking(false);
    }
  };

  // Al hacer clic en un bloque del calendario
  const handleEventSelect = (event) => {
    if (isBooking) return;
    if (event?.resource?.kind === 'availableSlot') {
      handleSlotSelect({ start: event.start });
    }
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
  };

  const handleBooking = async () => {
    if (!selectedService || !selectedSlot) {
      toast.error('❌ Por favor selecciona un servicio');
      return;
    }

    const start = selectedSlot.start;
    const end = new Date(start.getTime() + (typeof selectedService?.duration === 'number' ? selectedService.duration : 30) * 60000);

    if (hasOverlap(start, end)) {
      toast.error('❌ Este horario ya está ocupado');
      return;
    }

    if (isBooking) return;
    setLoading(true);
    setIsBooking(true);
    try {
      const timeStr = format(start, 'HH:mm');

      // Crear mediante Cloud Function con asignación por empleado/capacidad
      const dateStr = format(start, 'yyyy-MM-dd');
      console.log('🔄 ClientBooking - Iniciando reserva de cita...');
      console.log('🔄 ClientBooking - Datos de la cita:', {
        dateStr,
        time: timeStr,
        serviceId: selectedService.id,
        employeeId: selectedEmployeeId || null,
        type: selectedService.name,
        duration: typeof selectedService?.duration === 'number' ? selectedService.duration : 30,
        price: selectedService.price,
        userName: currentUser.displayName || 'Usuario',
        userEmail: currentUser.email,
        tenantId: tenantId,
        clientName: currentUser.displayName || 'Usuario',
        serviceName: selectedService.name
      });

      try { trackEvent('booking_submit', { tenantId, serviceId: selectedService.id, employeeId: selectedEmployeeId || null }); } catch { }

      await bookSlotViaFunction({
        dateStr,
        time: timeStr,
        serviceId: selectedService.id,
        employeeId: selectedEmployeeId || null,
        type: selectedService.name,
        duration: typeof selectedService?.duration === 'number' ? selectedService.duration : 30,
        price: selectedService.price,
        // Datos del usuario efectivo (cliente o impersonado por admin)
        userName: effectiveUserName || currentUser.displayName || 'Usuario',
        userEmail: effectiveUserEmail || currentUser.email || '',
        userPhone: effectiveUserPhone || userInfo?.phone || '',
        tenantId: tenantId,
        clientName: effectiveUserName || currentUser.displayName || 'Usuario',
        serviceName: selectedService.name,
        // Garantizar que la cita se asigne al usuario objetivo
        targetUserId: effectiveUserId,
        userId: effectiveUserId
      });

      toast.success('✅ Cita reservada exitosamente');
      try { trackEvent('appointment_created', { tenantId, serviceId: selectedService.id, employeeId: selectedEmployeeId || null, source: 'client_ui' }); } catch { }
      // No ocultar el slot tras reservar
      try { localStorage.removeItem('hiddenSlotsV1'); } catch { }
      try { await refreshMyAppointments(); } catch { }
      setShowBookingModal(false);
      setSelectedSlot(null);
      setSelectedService(null);
      setSelectedEmployeeId(null);
      setMobileStep(1);
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { }
    } catch (error) {
      toast.error('❌ Error al reservar la cita');
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setIsBooking(false);
    }
  };



  // Estilo personalizado por tipo de servicio
  const eventPropGetter = (event) => {
    // Slots disponibles
    if (event.resource?.kind === 'availableSlot') {
      // mostrar badge de plazas libres
      const left = Math.max(0, (event.resource.capacity || 1) - (event.resource.taken || 0));
      return {
        style: {
          backgroundColor: '#34d399',
          borderRadius: '8px',
          border: 'none',
          color: '#064e3b',
          opacity: 0.85,
          padding: '2px 6px',
          fontSize: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
        },
        children: (
          <div className="flex items-center justify-between">
            <span>{format(event.start, 'HH:mm')}</span>
            <span className="ml-2 text-xs bg-white/70 rounded px-1">{left} plaza{left === 1 ? '' : 's'}</span>
          </div>
        )
      };
    }

    // Citas del usuario: colores por tipo
    let backgroundColor;
    if (event.resource?.type?.includes('Gel')) {
      backgroundColor = '#ec4899';
    } else if (event.resource?.type?.includes('Acrílico')) {
      backgroundColor = '#8b5cf6';
    } else if (event.resource?.type?.includes('Personalizado')) {
      backgroundColor = '#f59e0b';
    } else {
      backgroundColor = '#3b82f6';
    }
    return {
      style: {
        backgroundColor,
        borderRadius: '8px',
        border: 'none',
        color: 'white',
        padding: '4px 8px',
        fontSize: '12px',
        fontWeight: '500',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }
    };
  };

  // Componente personalizado para eventos del calendario
  const CustomEvent = ({ event }) => (
    <div className="flex flex-col leading-tight relative group">
      {/* Tipo + Nombre del cliente */}
      <span className="text-sm font-medium truncate">
        {event.resource?.userName || 'Usuario'}
      </span>

      {/* Rango horario */}
      <span className="text-xs opacity-90">
        {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
      </span>

      {/* Tipo de servicio */}
      <span className="text-xs opacity-90">
        {event.resource?.type || 'Servicio'}
      </span>
    </div>
  );

  // Estado para fecha seleccionada (móvil)
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeSlots, setTimeSlots] = useState([]);

  // Función para generar horarios disponibles (cada 10 min)
  const generateTimeSlots = async (date) => {
    const slots = [];
    const startHour = 9; // 9:00 AM
    const endHour = 20; // 8:00 PM

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 10) {
        // Construir hora local exacta usando y/m/d del día seleccionado
        const y = date.getFullYear();
        const mo = date.getMonth();
        const d = date.getDate();
        const slotTime = new Date(y, mo, d, hour, minute, 0, 0);
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

        try {
          // Verificar disponibilidad del slot (respetando empleado seleccionado y duración del servicio)
          const availability = await checkSlotAvailability(
            date,
            timeStr,
            typeof selectedService?.duration === 'number' ? selectedService.duration : 30,
            selectedEmployeeId || null
          );

          if (availability.isAvailable) {
            // Solo verificar solapamiento si hay un empleado específico seleccionado
            // Para "Indiferente", el backend ya maneja la capacidad correctamente
            const slotEnd = new Date(slotTime.getTime() + 10 * 60000); // 10 min
            const hasConflict = hasOverlap(slotTime, slotEnd);

            if (!hasConflict) {
              slots.push({
                time: slotTime,
                displayTime: timeStr,
                isAvailable: true,
                maxBookings: availability.maxBookings,
                currentBookings: availability.currentBookings
              });
            }
          }
        } catch (error) {
          console.error('Error verificando disponibilidad:', error);
          // Si hay error, usar lógica anterior solo si hay empleado seleccionado
          const slotEnd = new Date(slotTime.getTime() + 10 * 60000);
          const isAvailable = !hasOverlap(slotTime, slotEnd);

          if (isAvailable) {
            slots.push({
              time: slotTime,
              displayTime: timeStr,
              isAvailable: true,
              maxBookings: 1,
              currentBookings: 0
            });
          }
        }
      }
    }
    return slots;
  };

  // Cargar horarios cuando cambie la fecha (móvil y escritorio)
  useEffect(() => {
    const loadTimeSlots = async () => {
      const slots = await generateTimeSlots(selectedDate);
      setTimeSlots(slots);
    };
    loadTimeSlots();
  }, [selectedDate, selectedEmployeeId]);

  // Interfaz móvil paso a paso (1) Servicio -> (2) Profesional -> (3) Semana de horarios
  if (isMobile) {

    // Navegación de fechas
    const goToPreviousDay = () => {
      const prevDay = new Date(selectedDate);
      prevDay.setDate(prevDay.getDate() - 1);
      if (prevDay >= new Date()) {
        setSelectedDate(prevDay);
        setSelectedSlot(null);
        setSelectedService(null);
      }
    };

    const goToNextDay = () => {
      const nextDay = new Date(selectedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setSelectedDate(nextDay);
      setSelectedSlot(null);
      setSelectedService(null);
    };

    const now = new Date();
    const upcomingAppointments = appointments
      .filter(a => String((a.status || '').toLowerCase()) === 'pendiente')
      .filter(a => {
        const d = getAppointmentStart(a);
        return !!(d && d >= now);
      })
      .sort((a, b) => {
        const ad = getAppointmentStart(a) || new Date(8640000000000000);
        const bd = getAppointmentStart(b) || new Date(8640000000000000);
        return ad - bd;
      });

    return (
      <div className="min-h-screen bg-gradient-to-br from-warm-100 via-warm-50 to-white py-6">
        <div className="max-w-md mx-auto px-4">
          {/* Encabezado móvil premium */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent mb-4">{appName}</h1>
            {logoUrl && (
              <div className="mb-5">
                <img
                  src={logoUrl}
                  alt="Imagen de cabecera"
                  className="w-full max-h-40 object-contain mx-auto rounded-2xl"
                />
              </div>
            )}
            {welcomeMessageEnabled && welcomeMessage && (
              <div className="mb-4 p-4 bg-white/80 backdrop-blur-sm rounded-2xl border border-warm-200 shadow-soft text-left">
                <p className="text-sm text-surface-600 leading-relaxed">{welcomeMessage}</p>
              </div>
            )}
            
            {/* Steps indicator premium */}
            <div className="mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                      mobileStep === step 
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30' 
                        : mobileStep > step 
                          ? 'bg-primary-100 text-primary-600' 
                          : 'bg-warm-200 text-surface-400'
                    }`}>
                      {mobileStep > step ? '✓' : step}
                    </div>
                    {step < 3 && (
                      <div className={`w-8 h-0.5 mx-1 transition-colors ${mobileStep > step ? 'bg-primary-400' : 'bg-warm-200'}`} />
                    )}
                  </div>
                ))}
              </div>
              <p className="text-surface-500 text-sm">{mobileStep === 1 ? 'Servicio' : mobileStep === 2 ? 'Profesional' : 'Horario'}</p>
            </div>

            {/* Indicador de límite de citas premium */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-warm-200 shadow-sm">
              <FiCalendar className="text-primary-500" />
              <span className="text-sm text-surface-600">
                Citas: <span className="font-semibold text-surface-800">{appointmentLimit.currentCount}</span>/{appointmentLimit.maxAllowed}
              </span>
              {!appointmentLimit.canBook && (
                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">Límite</span>
              )}
            </div>
          </div>

          {/* Paso 1: Servicio - Premium Mobile */}
          {mobileStep === 1 && (
            <div className="mb-6 animate-fade-in">
              <div className="text-center mb-5">
                <h3 className="text-xl font-bold text-gray-900 mb-1">¿Qué servicio necesitas?</h3>
                <p className="text-gray-500 text-sm">Selecciona el tratamiento que deseas</p>
              </div>
              <div className="space-y-3">
                {services.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => { setSelectedService(service); setMobileStep(2); }}
                    className="group relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 shadow-md hover:shadow-xl active:scale-[0.98] border border-gray-100"
                  >
                    <div className="flex">
                      {/* Imagen */}
                      <div className="relative w-28 h-28 flex-shrink-0">
                        {service.imageUrl ? (
                          <img 
                            src={service.imageUrl} 
                            alt={service.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600 flex items-center justify-center">
                            <span className="text-4xl">✨</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Contenido */}
                      <div className="flex-1 p-4 flex flex-col justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900 text-base mb-0.5">
                            {service.name}
                          </h4>
                          {service.description && (
                            <p className="text-gray-500 text-xs line-clamp-2">
                              {service.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1 text-gray-400 text-xs">
                            <FiClock className="w-3 h-3" />
                            <span>{service.duration} min</span>
                          </div>
                          <span className="text-lg font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">
                            {service.price}€
                          </span>
                        </div>
                      </div>
                      
                      {/* Flecha */}
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-primary-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Paso 2: Profesional - Premium */}
          {mobileStep === 2 && (
            <div className="bg-white rounded-2xl shadow-soft border border-warm-200/50 p-5 mb-6 animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <button 
                  onClick={() => { setSelectedEmployeeId(null); setMobileStep(1); }} 
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  Servicio
                </button>
                <h3 className="text-lg font-semibold text-surface-800">Profesional</h3>
                <div className="w-16" />
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => { setSelectedEmployeeId(null); setMobileStep(3); }}
                  className="w-full p-4 rounded-xl bg-warm-100 text-surface-700 border-2 border-warm-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 text-left font-medium"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-warm-200 flex items-center justify-center">
                      <FiUsers className="w-5 h-5 text-surface-500" />
                    </div>
                    <span>Sin preferencia</span>
                  </div>
                </button>
                {employees.map((emp) => (
                  <button
                    key={emp.id}
                    onClick={() => { setSelectedEmployeeId(emp.id); setMobileStep(3); }}
                    className="w-full p-4 rounded-xl bg-white text-surface-700 border-2 border-warm-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 text-left"
                  >
                    <div className="flex items-center gap-3">
                      {emp.photoUrl ? (
                        <img 
                          src={emp.photoUrl} 
                          alt={emp.displayName || emp.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-md"
                        />
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md"
                          style={{ backgroundColor: emp.color || '#3b82f6' }}
                        >
                          {(emp.displayName || emp.name || 'E').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="font-medium">{emp.displayName || emp.name || emp.id}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Paso 3: Semana de horarios - Premium */}
          {mobileStep === 3 && (
            <div className="bg-white rounded-2xl shadow-soft border border-warm-200/50 p-5 mb-6 animate-fade-in">
              <div className="flex items-center justify-between mb-5">
                <button 
                  onClick={() => setMobileStep(2)} 
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  Atrás
                </button>
                <div className="text-center">
                  <div className="text-sm font-semibold text-surface-800">
                    {format(weekStart, 'd MMM', { locale: es })} - {format(weekEnd, 'd MMM', { locale: es })}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button 
                    onClick={() => setWeekStart(addDays(weekStart, -7))} 
                    className="p-2 rounded-lg bg-warm-100 hover:bg-warm-200 text-surface-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <button 
                    onClick={() => setWeekStart(addDays(weekStart, 7))} 
                    className="p-2 rounded-lg bg-warm-100 hover:bg-warm-200 text-surface-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                {Array.from({ length: 7 }).map((_, i) => {
                  const day = addDays(weekStart, i);
                  const dateKey = format(day, 'yyyy-MM-dd');
                  const dateData = availableDates[dateKey];
                  const now = new Date();
                  const eff = effectiveByDate[dateKey];
                  // Unir disponibilidad administrativa (availableDates) con la efectiva
                  const baseSlotsM = Array.isArray(dateData?.slots) ? dateData.slots : [];
                  const effSlotsM = Array.isArray(eff) ? eff.map(t => ({ time: t })) : [];
                  const timeMapM = new Map();
                  baseSlotsM.forEach((s) => {
                    const t = typeof s === 'string' ? s : s?.time;
                    if (!t || !isValidTimeString(t)) return;
                    timeMapM.set(t, typeof s === 'object' ? s : { time: t });
                  });
                  effSlotsM.forEach((s) => {
                    const t = typeof s === 'string' ? s : s?.time;
                    if (!t || !isValidTimeString(t)) return;
                    if (!timeMapM.has(t)) timeMapM.set(t, typeof s === 'object' ? s : { time: t });
                  });
                  const rawSlots = Array.from(timeMapM.values());
                  // Ocultado local desactivado: no aplicar hiddenSlotsV1
                  const hiddenSlots = {};
                  const myTakenSet = new Set(
                    (appointments || []).filter(a => !isCancelled(a)).map((a) => {
                      const d = getAppointmentStart(a);
                      const ds = a.dateStr || (d ? format(d, 'yyyy-MM-dd') : '');
                      const parts = (a.time || '').split(':');
                      const ts = (parts.length >= 2)
                        ? `${String(parseInt(parts[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(parts[1] || '0', 10)).padStart(2, '0')}`
                        : (d ? format(d, 'HH:mm') : '');
                      return `${ds} ${ts}`;
                    })
                  );
                  // Importante: no usar allAppointments para “mis tramos ocupados”
                  // para evitar marcar como ocupado tras cancelar hasta que llegue el refresco global.
                  // Construir set de sub-tramos ocupados por MIS citas (bloqueo por duración)
                  const myBusySetMobile = new Set();
                  try {
                    const collectMine = (list) => {
                      (list || []).filter(a => a && !isCancelled(a) && ((a.userId && currentUser && a.userId === currentUser.uid) || (a.userEmail && currentUser && a.userEmail.toLowerCase() === currentUser.email.toLowerCase()))).forEach((a) => {
                        const baseDate = a.date?.toDate ? a.date.toDate() : (a.dateStr && a.time
                          ? new Date(parseInt(a.dateStr.split('-')[0], 10), parseInt(a.dateStr.split('-')[1], 10) - 1, parseInt(a.dateStr.split('-')[2], 10), parseInt(a.time.split(':')[0], 10), parseInt(a.time.split(':')[1], 10), 0, 0)
                          : new Date(a.date));
                        if (!baseDate || isNaN(baseDate.getTime())) return;
                        const dur = typeof a.duration === 'number' ? a.duration : 30;
                        for (let m = 0; m < dur; m += 10) {
                          const st = new Date(baseDate.getTime() + m * 60000);
                          myBusySetMobile.add(`${format(st, 'yyyy-MM-dd')} ${format(st, 'HH:mm')}`);
                        }
                      });
                    };
                    collectMine(appointments);
                    collectMine(allAppointments);
                  } catch { }

                  const daySlots = rawSlots
                    .map((s) => {
                      const timeStr = typeof s === 'string' ? s : s?.time;
                      if (!timeStr || !isValidTimeString(timeStr)) return null;
                      const p = String(timeStr).split(':');
                      const timeNorm = `${String(parseInt(p[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(p[1] || '0', 10)).padStart(2, '0')}`;
                      // Si los slots vienen de disponibilidad efectiva (solo tiempos),
                      // buscamos el slot original en availableDates para recuperar
                      // capacidad y empleados permitidos
                      const slotObj = Array.isArray(dateData?.slots)
                        ? dateData.slots.find((x) => {
                          const t = typeof x === 'string' ? x : x?.time;
                          return t === timeNorm;
                        })
                        : null;
                      // Bloqueo por duración para mis citas
                      if (myBusySetMobile.has(`${dateKey} ${timeNorm}`)) return null;
                      // Ocultar si habría solapamiento con mis citas al elegir este tramo
                      {
                        const candStart = new Date(`${dateKey}T${timeNorm}:00`);
                        const selDur2 = typeof selectedService?.duration === 'number' ? selectedService.duration : 30;
                        if (hasOverlap(candStart, new Date(candStart.getTime() + selDur2 * 60000))) return null;
                      }
                      // Sin ocultado local
                      const capacity = (typeof s === 'object' && s?.capacity != null)
                        ? Number(s.capacity)
                        : (typeof slotObj === 'object' && slotObj?.capacity != null)
                          ? Number(slotObj.capacity)
                          : Number(dateData?.defaultCapacity || employees.length || 1);
                      const allowedEmployees = (typeof s === 'object' && Array.isArray(s.employees) && s.employees.length > 0)
                        ? s.employees
                        : ((typeof slotObj === 'object' && Array.isArray(slotObj?.employees) && slotObj.employees.length > 0)
                          ? slotObj.employees
                          : null);
                      if (selectedEmployeeId && allowedEmployees && !allowedEmployees.includes(selectedEmployeeId)) return null;
                      const aptsThisSlot = allAppointments.filter(a => {
                        const st = String((a.status || '')).toLowerCase().trim();
                        if (!(st === 'pendiente' || st === 'pending' || st === 'confirmed')) return false;
                        const dKey = a.dateStr || format((a.date?.toDate ? a.date.toDate() : new Date(a.date)), 'yyyy-MM-dd');
                        const partsA = (a.time || '').split(':');
                        const tKey = (partsA.length >= 2)
                          ? `${String(parseInt(partsA[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(partsA[1] || '0', 10)).padStart(2, '0')}`
                          : format((a.date?.toDate ? a.date.toDate() : new Date(a.date)), 'HH:mm');
                        // Si es mi cita a esa hora, considerar ocupado incluso si capacidad > 1, para que me desaparezca
                        if (currentUser && ((a.userId === currentUser.uid) || (a.userEmail && a.userEmail.toLowerCase() === currentUser.email.toLowerCase()))) {
                          if (dKey === dateKey && tKey === timeNorm) return true;
                        }
                        return dKey === dateKey && tKey === timeNorm;
                      });
                      const totalBooked = aptsThisSlot.length;
                      const slotStart = new Date(`${dateKey}T${timeNorm}:00`);
                      const slotEnd = new Date(slotStart.getTime() + 30 * 60000);
                      // Si ya tengo una cita a esta hora, ocultar
                      if (myTakenSet.has(`${dateKey} ${timeNorm}`)) return null;
                      const selEmp = selectedEmployeeId ? (employees || []).find(e => e.id === selectedEmployeeId) : null;
                      const selName = selEmp?.displayName || selEmp?.name || null;
                      const employeeBusy = selectedEmployeeId ? allAppointments.some(a => {
                        const st2 = String((a.status || '')).toLowerCase().trim();
                        if (!(st2 === 'pendiente' || st2 === 'pending' || st2 === 'confirmed')) return false;
                        const dKey = a.dateStr || format((a.date?.toDate ? a.date.toDate() : new Date(a.date)), 'yyyy-MM-dd');
                        if (dKey !== dateKey) return false;
                        const sameEmployee = (a.employeeId && a.employeeId === selectedEmployeeId)
                          || (!!selName && !!a.employeeName && String(a.employeeName).toLowerCase() === String(selName).toLowerCase());
                        if (!sameEmployee) return false;
                        const aStart = (a.dateStr && a.time)
                          ? new Date(`${a.dateStr}T${a.time}:00`)
                          : (a.date?.toDate ? a.date.toDate() : new Date(a.date));
                        const aEnd = new Date(aStart.getTime() + (typeof a.duration === 'number' ? a.duration : 30) * 60000);
                        return slotStart < aEnd && slotEnd > aStart;
                      }) : false;
                      if (totalBooked >= capacity) return null;
                      if (selectedEmployeeId && employeeBusy) return null;
                      // No filtramos horas pasadas aquí: las mostraremos deshabilitadas en el botón
                      return timeNorm;
                    })
                    .filter(Boolean)
                    .sort();
                  return (
                    <div key={dateKey}>
                      <div className="text-center text-gray-800 font-medium mb-2">
                        {format(day, 'EEEE d', { locale: es })}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {daySlots.length > 0 ? daySlots.map((t) => {
                          const isSelected = selectedTimeSlot === `${dateKey} ${t}`;
                          const isProcessing = isBooking && isSelected;
                          const isPast = new Date(`${dateKey}T${t}:00`) < now;
                          const candStart = new Date(`${dateKey}T${t}:00`);
                          const selDurBtn = typeof selectedService?.duration === 'number' ? selectedService.duration : 30;
                          const conflictUser = myBusySetMobile.has(`${dateKey} ${t}`) || hasOverlap(candStart, new Date(candStart.getTime() + selDurBtn * 60000));
                          return (
                            <button
                              key={`${dateKey}-${t}`}
                              onClick={() => handleSlotSelect({ start: new Date(`${dateKey}T${t}:00`) })}
                              disabled={isBooking || isPast || conflictUser}
                              className={`p-3 text-sm font-medium rounded-lg transition-all duration-200 ${isProcessing
                                ? 'bg-blue-500 text-white animate-pulse'
                                : conflictUser
                                  ? 'bg-red-100 text-red-700 border border-red-300'
                                  : isSelected
                                    ? 'bg-blue-600 text-white'
                                    : isPast
                                      ? 'bg-gray-100 text-gray-400'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                } ${isBooking || conflictUser ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                              {isProcessing ? '🔄' : t}
                            </button>
                          );
                        }) : (
                          <div className="col-span-3 text-center text-sm text-gray-400">No hay horas</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* En el flujo por pasos, la confirmación ocurre en el modal tras elegir hora */}

          {/* Próximas citas (móvil) - Premium */}
          {upcomingAppointments.length > 0 && (
            <div className="bg-white rounded-2xl shadow-soft border border-warm-200/50 p-5 mt-6">
              <h3 className="text-lg font-semibold text-surface-800 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <FiCalendar className="w-4 h-4 text-primary-600" />
                </div>
                Tus próximas citas
              </h3>
              <div className="space-y-3">
                {upcomingAppointments.map(apt => {
                  const date = getAppointmentStart(apt) || new Date();
                  return (
                    <div key={apt.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-warm-50 to-warm-100/50 rounded-xl border border-warm-200/50">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex flex-col items-center justify-center border border-warm-200">
                          <span className="text-xs font-medium text-surface-500">{format(date, 'MMM', { locale: es }).toUpperCase()}</span>
                          <span className="text-lg font-bold text-surface-800">{format(date, 'd')}</span>
                        </div>
                        <div>
                          <div className="font-semibold text-surface-800">{apt.type}</div>
                          <div className="text-sm text-surface-500">{format(date, 'HH:mm')} h</div>
                          {(() => {
                            try {
                              const emp = (employees || []).find(e => e.id === apt.employeeId);
                              const label = apt.employeeName || emp?.displayName || emp?.name || null;
                              return label ? (
                                <div className="text-xs text-surface-400">con {label}</div>
                              ) : null;
                            } catch { return null; }
                          })()}
                        </div>
                      </div>
                      <button
                        onClick={() => requestCancelAppointment(apt.id)}
                        className="p-2.5 rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Cancelar cita"
                      >
                        <FiX className="h-5 w-5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Modal de reserva (móvil) - Premium */}
          {showBookingModal && selectedSlot && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-end sm:items-center z-50" onClick={() => setShowBookingModal(false)}>
              <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto animate-slide-up" onClick={e => e.stopPropagation()}>
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-surface-800">{t('booking.book_appointment') || 'Confirmar Reserva'}</h3>
                    <button 
                      onClick={() => setShowBookingModal(false)}
                      className="p-2 rounded-xl hover:bg-warm-100 text-surface-400 hover:text-surface-600 transition-colors"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Fecha y hora */}
                  <div className="mb-5 p-4 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-2xl border border-primary-200">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex flex-col items-center justify-center border border-primary-200">
                        <span className="text-xs font-medium text-primary-500">{format(selectedSlot.start, 'MMM', { locale: es }).toUpperCase()}</span>
                        <span className="text-xl font-bold text-surface-800">{format(selectedSlot.start, 'd')}</span>
                      </div>
                      <div>
                        <div className="text-sm text-surface-500">Fecha y hora</div>
                        <div className="text-lg font-semibold text-surface-800">{format(selectedSlot.start, "EEEE d 'de' MMMM", { locale: es })}</div>
                        <div className="text-primary-600 font-semibold">{format(selectedSlot.start, 'HH:mm')} h</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Servicio seleccionado */}
                  {selectedService ? (
                    <div className="mb-6 p-4 bg-warm-50 rounded-2xl border border-warm-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-surface-500 mb-1">Servicio</div>
                          <div className="font-semibold text-surface-800">{selectedService.name}</div>
                          {selectedService.description && (
                            <div className="text-sm text-surface-500 mt-1">{selectedService.description}</div>
                          )}
                        </div>
                        <div className="text-xl font-bold text-primary-600">€{selectedService.price}</div>
                      </div>
                      <button
                        className="mt-3 text-sm text-primary-700 hover:underline"
                        onClick={() => { setShowBookingModal(false); setMobileStep(1); }}
                      >
                        Cambiar servicio
                      </button>
                    </div>
                  ) : (
                    <div className="mb-6 text-sm text-red-600">
                      Debes seleccionar un servicio. Pulsa "Cambiar servicio" para volver al paso 1.
                      <div>
                        <button className="mt-2 text-primary-700 hover:underline" onClick={() => { setShowBookingModal(false); setMobileStep(1); }}>Cambiar servicio</button>
                      </div>
                    </div>
                  )}
                  <div className="flex space-x-3">
                    <button onClick={() => setShowBookingModal(false)} className="btn-secondary flex-1">Cancelar</button>
                    <button onClick={handleBooking} disabled={!selectedService || loading} className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed">
                      {loading ? (t('booking.booking') || 'Reservando...') : ('✅ ' + (t('booking.confirm_booking') || 'Confirmar Reserva'))}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Modal de confirmación de cancelación - Premium */}
        {showConfirmCancel && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4" onClick={() => setShowConfirmCancel(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full animate-scale-in" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <FiX className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-surface-800 mb-2">¿Cancelar cita?</h3>
                <p className="text-surface-500 mb-6">Esta acción no se puede deshacer.</p>
                <div className="flex gap-3">
                  <button onClick={() => setShowConfirmCancel(false)} className="btn-secondary flex-1">No, volver</button>
                  <button onClick={performCancelAppointment} className="btn-danger flex-1">Sí, cancelar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer legal móvil - usa modal en lugar de enlaces externos para PWA */}
        <LegalFooter compact={true} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Inyectar estilos personalizados */}
      <style>{customStyles}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header personalizado */}
        <div className="mb-8 text-center">
          {/* Nombre de la empresa siempre arriba */}
          <h1 className="text-4xl font-bold text-primary-600 mb-4">{appName}</h1>

          {/* Imagen: en móvil ocupa todo el ancho; en escritorio, logo compacto */}
          {logoUrl && (
            isMobile ? (
              <div className="mb-6">
                <img
                  src={logoUrl}
                  alt="Imagen de cabecera"
                  className="w-full max-h-48 object-contain mx-auto"
                />
              </div>
            ) : (
              <div className="mb-6">
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="h-32 w-32 mx-auto object-contain"
                />
              </div>
            )
          )}

          <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('booking.book_appointment') || 'Reservar Cita'}</h2>

          {/* Indicador de límite de citas */}
          <div className="max-w-md mx-auto mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-center space-x-2">
              <FiCalendar className="text-blue-600" />
              <span className="text-sm text-blue-800">
                Citas activas: <span className="font-semibold">{appointmentLimit.currentCount}</span> / {appointmentLimit.maxAllowed}
              </span>
              {!appointmentLimit.canBook && (
                <span className="text-xs text-red-600 font-medium">(Límite alcanzado)</span>
              )}
            </div>
          </div>

          {/* Bloque de diagnóstico eliminado */}

          {welcomeMessageEnabled && welcomeMessage && (
            <div className="max-w-3xl mx-auto mb-4 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg border border-primary-200">
              <p className="text-lg text-gray-700 leading-relaxed">{welcomeMessage}</p>
            </div>
          )}
          <p className="text-gray-600">Sigue los pasos: Servicio → Profesional → Horario</p>
        </div>

        {/* Navegación rápida entre pasos (solo móvil) */}
        {isMobile && mobileStep > 1 && (
          <div className="mb-4 flex items-center gap-2">
            {mobileStep >= 2 && (
              <button onClick={() => setMobileStep(1)} className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50">← Servicio</button>
            )}
            {mobileStep >= 3 && (
              <button onClick={() => setMobileStep(2)} className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50">← Profesional</button>
            )}
          </div>
        )}

        {/* Paso 1: Servicio (escritorio) - Diseño Premium */}
        {(!isMobile && mobileStep === 1) && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 max-w-4xl mx-auto border border-gray-100">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">¿Qué servicio necesitas?</h3>
              <p className="text-gray-500">Selecciona el tratamiento que deseas reservar</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((service) => (
                <div
                  key={service.id}
                  onClick={() => { setSelectedService(service); setMobileStep(2); }}
                  className="group relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border border-gray-200 hover:border-primary-300"
                >
                  {/* Imagen del servicio */}
                  <div className="relative h-40 overflow-hidden">
                    {service.imageUrl ? (
                      <img 
                        src={service.imageUrl} 
                        alt={service.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600 flex items-center justify-center">
                        <div className="text-center">
                          <span className="text-5xl mb-2 block">✨</span>
                          <span className="text-white/80 text-sm font-medium">Tratamiento</span>
                        </div>
                      </div>
                    )}
                    {/* Overlay con precio */}
                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-primary-600 font-bold text-sm shadow-lg">
                        {service.price}€
                      </span>
                    </div>
                  </div>
                  
                  {/* Contenido */}
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 text-lg mb-1 group-hover:text-primary-600 transition-colors">
                      {service.name}
                    </h4>
                    {service.description && (
                      <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                        {service.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-gray-400 text-sm">
                        <FiClock className="w-4 h-4" />
                        <span>{service.duration} min</span>
                      </div>
                      <span className="text-primary-600 text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                        Reservar
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Paso 2: Profesional (escritorio) - Premium */}
        {(!isMobile && mobileStep === 2) && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 max-w-3xl mx-auto border border-gray-100">
            <div className="text-center mb-6">
              <button onClick={() => { setMobileStep(1); setSelectedEmployeeId(null); }} className="text-sm text-primary-600 hover:text-primary-700 mb-2">← Volver a servicios</button>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">¿Con quién prefieres?</h3>
              <p className="text-gray-500">Selecciona un profesional o déjanos elegir por ti</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {/* Opción Sin preferencia */}
              <div
                onClick={() => { setSelectedEmployeeId(null); setMobileStep(3); }}
                className="group p-5 rounded-2xl bg-gray-50 border-2 border-gray-200 hover:border-primary-400 hover:bg-primary-50 cursor-pointer transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-gray-200 flex items-center justify-center mb-3">
                  <FiUsers className="w-7 h-7 text-gray-500" />
                </div>
                <h4 className="font-semibold text-gray-900 group-hover:text-primary-700">Sin preferencia</h4>
                <p className="text-sm text-gray-500 mt-1">Cualquier profesional disponible</p>
              </div>
              
              {/* Lista de empleados */}
              {employees.map((emp) => (
                <div
                  key={emp.id}
                  onClick={() => { setSelectedEmployeeId(emp.id); setMobileStep(3); }}
                  className="group p-5 rounded-2xl bg-white border-2 border-gray-200 hover:border-primary-400 hover:shadow-lg cursor-pointer transition-all duration-300 text-center"
                >
                  {emp.photoUrl ? (
                    <img 
                      src={emp.photoUrl} 
                      alt={emp.displayName || emp.name}
                      className="w-16 h-16 mx-auto rounded-full object-cover border-3 border-white shadow-lg mb-3"
                    />
                  ) : (
                    <div 
                      className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg mb-3"
                      style={{ backgroundColor: emp.color || '#3b82f6' }}
                    >
                      {(emp.displayName || emp.name || 'E').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <h4 className="font-semibold text-gray-900 group-hover:text-primary-700">
                    {emp.displayName || emp.name || emp.id}
                  </h4>
                  <span className="inline-flex items-center gap-1 text-xs text-green-600 mt-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    Disponible
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Paso 3 en escritorio: usar la misma vista de lista que móvil */}
        {(!isMobile && mobileStep === 3) && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setMobileStep(2)} className="text-sm text-primary-600">← Profesional</button>
              <div className="text-center font-medium">
                {format(weekStart, 'd MMM', { locale: es })} - {format(weekEnd, 'd MMM', { locale: es })}
              </div>
              <div className="flex space-x-2">
                <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="px-2 py-1 bg-gray-100 rounded">←</button>
                <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="px-2 py-1 bg-gray-100 rounded">→</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {Array.from({ length: 7 }).map((_, i) => {
                const day = addDays(weekStart, i);
                const startToday = new Date();
                startToday.setHours(0, 0, 0, 0);
                if (day < startToday) return null;
                const dateKey = format(day, 'yyyy-MM-dd');
                const dateData = availableDates[dateKey];
                const now = new Date();
                const eff2 = effectiveByDate[dateKey];
                // Unir disponibilidad "administrativa" (availableDates) con la "efectiva" (eff2)
                const baseSlots = Array.isArray(dateData?.slots) ? dateData.slots : [];
                const effSlots = Array.isArray(eff2) ? eff2.map(t => ({ time: t })) : [];
                const timeMap = new Map();
                baseSlots.forEach((s) => {
                  const t = typeof s === 'string' ? s : s?.time;
                  if (!t || !isValidTimeString(t)) return;
                  timeMap.set(t, typeof s === 'object' ? s : { time: t });
                });
                effSlots.forEach((s) => {
                  const t = typeof s === 'string' ? s : s?.time;
                  if (!t || !isValidTimeString(t)) return;
                  if (!timeMap.has(t)) timeMap.set(t, typeof s === 'object' ? s : { time: t });
                });
                const rawSlots = Array.from(timeMap.values());
                // Ocultado local desactivado
                const hiddenSlots = {};
                const myTakenSet = new Set(
                  (appointments || []).map((a) => {
                    const d = getAppointmentStart(a);
                    const ds = a.dateStr || (d ? format(d, 'yyyy-MM-dd') : '');
                    const parts = (a.time || '').split(':');
                    const ts = (parts.length >= 2)
                      ? `${String(parseInt(parts[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(parts[1] || '0', 10)).padStart(2, '0')}`
                      : (d ? format(d, 'HH:mm') : '');
                    return `${ds} ${ts}`;
                  })
                );
                try {
                  (allAppointments || []).forEach(a => {
                    if (a.status === 'cancelada') return;
                    const isMine = (a.userId && currentUser && a.userId === currentUser.uid) || (a.userEmail && currentUser && a.userEmail.toLowerCase() === currentUser.email.toLowerCase());
                    if (!isMine) return;
                    const d = getAppointmentStart(a);
                    const ds = a.dateStr || (d ? format(d, 'yyyy-MM-dd') : '');
                    const parts = (a.time || '').split(':');
                    const ts = (parts.length >= 2)
                      ? `${String(parseInt(parts[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(parts[1] || '0', 10)).padStart(2, '0')}`
                      : (d ? format(d, 'HH:mm') : '');
                    myTakenSet.add(`${ds} ${ts}`);
                  });
                } catch { }
                // Calcular tramos ocupados por el profesional seleccionado (por id o nombre)
                const busyByEmployeeSet = new Set();
                if (selectedEmployeeId) {
                  try {
                    const selEmp = (employees || []).find(e => e.id === selectedEmployeeId) || null;
                    const selName = selEmp?.displayName || selEmp?.name || null;
                    (allAppointments || []).forEach((a) => {
                      if (isCancelled(a)) return;
                      const st = String((a.status || '').toLowerCase());
                      if (!(st === 'pendiente' || st === 'pending')) return;
                      const dKey = a.dateStr || format((a.date?.toDate ? a.date.toDate() : new Date(a.date)), 'yyyy-MM-dd');
                      if (dKey !== dateKey) return;
                      const sameEmployee = (a.employeeId && a.employeeId === selectedEmployeeId)
                        || (!!selName && !!a.employeeName && String(a.employeeName).toLowerCase() === String(selName).toLowerCase());
                      if (!sameEmployee) return;
                      const aStart = (a.dateStr && a.time)
                        ? new Date(`${a.dateStr}T${a.time}:00`)
                        : (a.date?.toDate ? a.date.toDate() : new Date(a.date));
                      const aDur = Number.isFinite(a.duration) ? Number(a.duration) : 30;
                      for (let m = 0; m < aDur; m += 10) {
                        const t = new Date(aStart.getTime() + m * 60000);
                        busyByEmployeeSet.add(`${format(t, 'yyyy-MM-dd')} ${format(t, 'HH:mm')}`);
                      }
                    });
                  } catch { }
                }

                const daySlots = rawSlots
                  .map((s) => {
                    const timeStr = typeof s === 'string' ? s : s?.time;
                    if (!timeStr || !isValidTimeString(timeStr)) return null;
                    const p = String(timeStr).split(':');
                    const timeNorm = `${String(parseInt(p[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(p[1] || '0', 10)).padStart(2, '0')}`;
                    if (selectedEmployeeId && busyByEmployeeSet.has(`${dateKey} ${timeNorm}`)) return null;
                    // Recuperar slot original si viene de disponibilidad efectiva
                    const slotObj = Array.isArray(dateData?.slots)
                      ? dateData.slots.find((x) => {
                        const t = typeof x === 'string' ? x : x?.time;
                        return t === timeNorm;
                      })
                      : null;
                    // Sin ocultado local
                    const capacity = (typeof s === 'object' && s?.capacity != null)
                      ? Number(s.capacity)
                      : (typeof slotObj === 'object' && slotObj?.capacity != null)
                        ? Number(slotObj.capacity)
                        : Number(dateData?.defaultCapacity || employees.length || 1);
                    const allowedEmployees = (typeof s === 'object' && Array.isArray(s.employees) && s.employees.length > 0)
                      ? s.employees
                      : ((typeof slotObj === 'object' && Array.isArray(slotObj?.employees) && slotObj.employees.length > 0)
                        ? slotObj.employees
                        : null);
                    if (selectedEmployeeId && allowedEmployees && !allowedEmployees.includes(selectedEmployeeId)) return null;
                    const aptsThisSlot = allAppointments.filter(a => {
                      const st = String((a.status || '')).toLowerCase().trim();
                      // contar solo activas
                      if (!(st === 'pendiente' || st === 'pending' || st === 'confirmed')) return false;
                      const dKey = a.dateStr || format((a.date?.toDate ? a.date.toDate() : new Date(a.date)), 'yyyy-MM-dd');
                      const partsA = (a.time || '').split(':');
                      const tKey = (partsA.length >= 2)
                        ? `${String(parseInt(partsA[0] || '0', 10)).padStart(2, '0')}:${String(parseInt(partsA[1] || '0', 10)).padStart(2, '0')}`
                        : format((a.date?.toDate ? a.date.toDate() : new Date(a.date)), 'HH:mm');
                      return dKey === dateKey && tKey === timeNorm;
                    });
                    const totalBooked = aptsThisSlot.length;
                    const slotStart = new Date(`${dateKey}T${timeNorm}:00`);
                    const slotEnd = new Date(slotStart.getTime() + 30 * 60000);
                    // Si ya tengo cita a esa hora, ocultar. Si acabo de liberarla, permitir verla.
                    if (myTakenSet.has(`${dateKey} ${timeNorm}`)) {
                      // Si la cita está cancelada en cualquiera de las fuentes, permitir ver el slot
                      const isStillActiveMine = (appointments || []).some(a => !isCancelled(a) && `${a.dateStr || format(getAppointmentStart(a), 'yyyy-MM-dd')} ${String(a.time || '').padStart(5, '0')}` === `${dateKey} ${timeNorm}`);
                      if (isStillActiveMine) return null;
                    }
                    // Ocultar por profesional ocupado (por id o nombre), SIEMPRE (también con disponibilidad efectiva)
                    const selEmp = selectedEmployeeId ? (employees || []).find(e => e.id === selectedEmployeeId) : null;
                    const selName = selEmp?.displayName || selEmp?.name || null;
                    const employeeBusy = selectedEmployeeId ? allAppointments.some(a => {
                      if (a.status === 'cancelada') return false;
                      const st2 = String((a.status || '').toLowerCase());
                      // Considerar ocupado solo para citas activas/pedidas
                      if (!(st2 === 'pendiente' || st2 === 'pending')) return false;
                      const dKey = a.dateStr || format((a.date?.toDate ? a.date.toDate() : new Date(a.date)), 'yyyy-MM-dd');
                      if (dKey !== dateKey) return false;
                      const sameEmployee = (a.employeeId && a.employeeId === selectedEmployeeId)
                        || (!!selName && !!a.employeeName && String(a.employeeName).toLowerCase() === String(selName).toLowerCase());
                      if (!sameEmployee) return false;
                      const aStart = (a.dateStr && a.time)
                        ? new Date(`${a.dateStr}T${a.time}:00`)
                        : (a.date?.toDate ? a.date.toDate() : new Date(a.date));
                      const aEnd = new Date(aStart.getTime() + (typeof a.duration === 'number' ? a.duration : 30) * 60000);
                      return slotStart < aEnd && slotEnd > aStart;
                    }) : false;
                    if (selectedEmployeeId && employeeBusy) return null;
                    if (totalBooked >= capacity) return null;
                    if (new Date(`${dateKey}T${timeNorm}:00`) < now) return null;
                    return timeNorm;
                  })
                  .filter(Boolean)
                  .sort();
                return (
                  <div key={dateKey} className="">
                    <div className="text-center text-gray-800 font-medium mb-2">
                      {format(day, 'EEEE d', { locale: es })}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {daySlots.length > 0 ? daySlots.map((t) => {
                        const isSelected = selectedTimeSlot === `${dateKey} ${t}`;
                        const isProcessing = isBooking && isSelected;
                        const isPast = new Date(`${dateKey}T${t}:00`) < now;
                        const candStart = new Date(`${dateKey}T${t}:00`);
                        const selDurBtn = typeof selectedService?.duration === 'number' ? selectedService.duration : 30;
                        const conflictUser = hasOverlap(candStart, new Date(candStart.getTime() + selDurBtn * 60000));
                        return (
                          <button
                            key={`${dateKey}-${t}`}
                            onClick={() => handleSlotSelect({ start: new Date(`${dateKey}T${t}:00`) })}
                            disabled={isBooking || isPast || conflictUser}
                            className={`p-3 text-sm font-medium rounded-lg transition-all duration-200 ${isProcessing
                              ? 'bg-blue-500 text-white animate-pulse'
                              : conflictUser
                                ? 'bg-red-100 text-red-700 border border-red-300'
                                : isSelected
                                  ? 'bg-blue-600 text-white'
                                  : isPast
                                    ? 'bg-gray-100 text-gray-400'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              } ${isBooking || conflictUser ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            {isProcessing ? '🔄' : t}
                          </button>
                        );
                      }) : (
                        <div className="col-span-4 text-center text-sm text-gray-400">No hay horas</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Mis citas pendientes (mostrar siempre las pendientes) */}
        {appointments.filter(a => {
          if (String((a.status || '').toLowerCase()) !== 'pendiente') return false;
          const d = getAppointmentStart(a);
          return !!(d && d >= new Date());
        }).length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">📋 Tus próximas citas</h2>
              <div className="space-y-3">
                {appointments
                  .filter(a => {
                    if (String((a.status || '').toLowerCase()) !== 'pendiente') return false;
                    const d = getAppointmentStart(a);
                    return !!(d && d >= new Date());
                  })
                  .map(apt => {
                    const date = getAppointmentStart(apt) || new Date();
                    return (
                      <div key={apt.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <FiCalendar className="text-primary-600" />
                          <div>
                            <div className="font-medium text-gray-900">{apt.type}</div>
                            <div className="text-sm text-gray-600">
                              {format(date, 'dd/MM/yyyy HH:mm')} - {apt.duration} min
                            </div>
                            {(() => {
                              try {
                                const emp = (employees || []).find(e => e.id === apt.employeeId);
                                const label = apt.employeeName || emp?.displayName || emp?.name || null;
                                return label ? (
                                  <div className="text-xs text-gray-600">Profesional: {label}</div>
                                ) : null;
                              } catch { return null; }
                            })()}
                          </div>
                        </div>
                        <button
                          onClick={() => requestCancelAppointment(apt.id)}
                          className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
                          title="Cancelar cita"
                        >
                          <FiX className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
      </div>

      {/* Modal de reserva */}
      {showBookingModal && selectedSlot && (
        <div className="modal-overlay" onClick={() => setShowBookingModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              {/* Header del modal */}
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-200">
                  <FiCalendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {t('booking.book_appointment') || 'Reservar Cita'}
                  </h3>
                  <p className="text-sm text-gray-500">Confirma los detalles de tu reserva</p>
                </div>
              </div>

              {/* Fecha seleccionada */}
              <div className="mb-5 p-4 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center">
                    <FiClock className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fecha y hora</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {format(selectedSlot.start, "EEEE, d 'de' MMMM", { locale: es })}
                      <span className="text-primary-600 ml-2">
                        {format(selectedSlot.start, 'HH:mm', { locale: es })}h
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {services.length > 0 ? (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('booking.select_service') || 'Selecciona el tipo de servicio:'}
                  </label>
                  <div className="grid grid-cols-1 gap-3 max-h-[400px] overflow-y-auto pr-1">
                    {services.map((service) => (
                      <div
                        key={service.id}
                        onClick={() => handleServiceSelect(service)}
                        className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                          selectedService?.id === service.id
                            ? 'ring-2 ring-primary-500 ring-offset-2 shadow-lg shadow-primary-100'
                            : 'border border-gray-200 hover:border-primary-300 hover:shadow-md'
                        }`}
                      >
                        <div className="flex">
                          {/* Imagen del servicio */}
                          <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0">
                            {service.imageUrl ? (
                              <img 
                                src={service.imageUrl} 
                                alt={service.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600 flex items-center justify-center">
                                <span className="text-3xl text-white/90">✨</span>
                              </div>
                            )}
                            {/* Overlay de selección */}
                            {selectedService?.id === service.id && (
                              <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center">
                                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Contenido */}
                          <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between bg-white">
                            <div>
                              <h4 className="font-semibold text-gray-900 text-sm sm:text-base leading-tight mb-1 group-hover:text-primary-700 transition-colors">
                                {service.name}
                              </h4>
                              {service.description && (
                                <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 leading-relaxed">
                                  {service.description}
                                </p>
                              )}
                            </div>
                            
                            {/* Precio y duración */}
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-1 text-xs text-gray-400">
                                <FiClock className="w-3 h-3" />
                                <span>{service.duration} min</span>
                              </div>
                              <div className="flex items-baseline gap-0.5">
                                <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">
                                  {service.price}€
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Borde degradado inferior cuando está seleccionado */}
                        {selectedService?.id === service.id && (
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">📋</span>
                  </div>
                  <p className="text-gray-500 font-medium">No hay servicios disponibles</p>
                  <p className="text-gray-400 text-sm mt-1">Contacta con el negocio para más información</p>
                </div>
              )}

              {/* Resumen de selección */}
              {selectedService && (
                <div className="mb-5 p-4 bg-primary-50 border border-primary-100 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {selectedService.imageUrl ? (
                        <img src={selectedService.imageUrl} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <span className="text-primary-600">✨</span>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-primary-600 font-medium">Servicio seleccionado</p>
                        <p className="font-semibold text-gray-900">{selectedService.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary-600">{selectedService.price}€</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Botones de acción */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBooking}
                  disabled={!selectedService || loading}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-xl shadow-lg shadow-primary-200 hover:shadow-xl hover:shadow-primary-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Reservando...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Confirmar Reserva
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal de confirmación de cancelación (branding Reserberry) */}
      {showConfirmCancel && (
        <div className="modal-overlay" onClick={() => setShowConfirmCancel(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Reserberry</h3>
              <p className="text-gray-700 mb-6">¿Estás seguro de que quieres cancelar esta cita?</p>
              <div className="flex space-x-3">
                <button onClick={() => setShowConfirmCancel(false)} className="btn-secondary flex-1">No, volver</button>
                <button onClick={performCancelAppointment} className="btn-primary flex-1">Sí, cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer legal desktop - usa modal en lugar de enlaces externos para PWA */}
      <LegalFooter compact={false} />
    </div>
  );
};

export default ClientBooking;
